import * as React from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PDFViewer from "@Components/PDFViewer/PDFViewer";
import { EditSectionPopup } from "../GenericForm/EditSectionPopup";
import EditIcon from "@mui/icons-material/Edit";
import useSendPutRequest from "../../Hooks/useSendPutRequest";
import {
  GenericOrderInfoStyles,
  LabelStyles,
  PreviewDocumentBoxStyles,
  SectionHeaderStyles,
  SubSectionStyles,
  TabsStyles,
  ValueStyles,
} from "./GenericOrderInfoStyles";
import CargoXList from "./CargoXList";
import ApprovalList from "./ApprovalsList";
import InvoiceList from "./InvoicesList";
import DocumentList from "./DocumentList";
import {
  Company,
  IncoTerm,
  inputTypes,
  MaturityType,
  Ports,
  State,
  wayOfShippingOptions,
  transactionType,
  loadability,
  yesOrNoChoice,
  origins,
} from "../../Utils/HelpingObjects/HelpingObjects";
import useGetManyRequests from "../../Hooks/useGetManyRequests";
import {
  CLEARANCE_AGENTS_URL,
  CATEGORIES_URL,
  MATERIALS_URL,
  SUPPLIERS_URL,
  PLANNERS_URL,
  SHIPPING_LINES_URL,
  SAVE_DOCUMENT,
  ORDER_PRIMITIVE_DATA_URL,
  ORDER_INFORMATION_URL,
  ORDER_FORM4_INFORMATION_URL,
  ORDER_SPECIFIC_INFORMATION_URL,
  ORDER_TRANSPORTATION_DETAILS_URL,
  BANKS_URL,
  WAREHOUSES_URL,
  STORAGE_LOCATIONS_URL,
  STAR_ORDERS_URL,
  UNSTAR_ORDERS_URL,
  PENDING_ORDER_COMMENTS,
  GET_LINKAGE_INVOICE_NUMBERS,
  RELEASE_ORDER,
  IS_ORDER_STARRED,
  FORM4_PDF_URL,
} from "../../API/APIRoutes";
import { useDispatch, useSelector } from "react-redux";
import ConditionTable from "./Condition";
import { options } from "joi";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import { useTheme } from "@mui/material/styles";
import { InsertDocumentPopup } from "../Documents/InsertDocumentPopup";
import axios from "axios";
import usePostItem from "@Hooks/usePostItem";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import ChatPopup from "../GenericOrderChat/ChatPopup";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import useGetItem from "../../Hooks/useGetItem";
import { ReleaseOrderPopup } from "../ReleaseOrderPopup/ReleaseOrderPopup";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import { encryptId } from "../../Utils/SecurityFunctions";
import TransportationSection from "./TransportationSection";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { GET_MSDS_FILE_FOR_ORDER } from "../../API/APIRoutes";
import { SimpleNotesPopup } from "@Components/SimpleNotesPopup/SimpleNotesPopup";
import VerifiedIcon from "@mui/icons-material/Verified";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axiosInstance from "@API/axios";
import { setSnackbar } from "@Redux/snackBarSlice";

// A reusable TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pl: 3, pr: 0 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Accessibility helper
function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const formatText = (state, capital = false) => {
  if (!state) return "";

  const formattedText = state
    .toString()
    .toLowerCase() // Convert to lowercase
    .replace(/_/g, " ") // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word

  return capital ? formattedText.toUpperCase() : formattedText; // Return all uppercase if capital is true
};

// A reusable info item that can display a label and a value
// Helper function to get nested value from an object using a dot-separated path
const getNestedValue = (obj, path) => {
  const keys = path.split(".");

  if (keys.length === 2 && Array.isArray(obj[keys[0]])) {
    return obj[keys[0]]
      .map((item) => item?.[keys[1]]?.trim().toUpperCase())
      .join(" - ");
  }

  // Handle normal nested access
  return keys.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

const convertArrayToISOString = (value) => {
  if (!Array.isArray(value) || value.length < 3) {
    return null;
  }

  const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = value;
  const date = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second, Math.floor(nano / 1e6))
  );

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const normalizeDateTimeValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) {
    return convertArrayToISOString(value);
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  if (typeof value === "string") {
    return value;
  }
  return null;
};

// A reusable info item that can display a label and a value
const InfoItem = ({ label, field, data, formatter }) => {
  const rawValue = getNestedValue(data, field);

  const formattedValue =
    typeof formatter === "function"
      ? formatter(rawValue)
      : rawValue !== undefined && rawValue !== null
      ? rawValue
      : "";

  const isEmpty = rawValue === null || rawValue === undefined || rawValue == "";

  return (
    <Grid container spacing={2} sx={{ mb: 4, pl: 0 }}>
      <Grid item xs={6.5}>
        <Typography variant="body1" sx={LabelStyles}>
          {label}:
        </Typography>
      </Grid>
      <Grid item xs={5.5}>
        <Typography variant="body1" sx={ValueStyles(isEmpty)}>
          {isEmpty ? "N/A" : formattedValue}
        </Typography>
      </Grid>
    </Grid>
  );
};

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  formatter: PropTypes.func,
};
InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  formatter: PropTypes.func,
};

// Section header with an optional close button (useful for the PDF viewer)
const SectionHeader = ({
  title,
  textAfterTitle = "",
  close,
  onClose,
  action,
}) => (
  <Box sx={SectionHeaderStyles(title === "Documents")}>
    <Typography
      variant="h6"
      sx={{
        color: "primary.main",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {title}
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          ml: 1, // small left margin
        }}
      >
        {textAfterTitle}
      </Typography>
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {action}
      {close && (
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  </Box>
);

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  close: PropTypes.bool,
  onClose: PropTypes.func,
};

// A subsection that renders a header and its children (e.g. a list of InfoItems)
const SubSection = ({
  title,
  textAfterTitle = "",
  children,
  width,
  mr,
  pr,
  close,
  onClose,
  action,
}) => (
  <Box sx={SubSectionStyles(width, pr, mr)}>
    <SectionHeader
      title={title}
      textAfterTitle={textAfterTitle}
      close={close}
      onClose={onClose}
      action={action}
    />
    {children}
  </Box>
);

SubSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  width: PropTypes.string,
  mr: PropTypes.number,
  pr: PropTypes.number,
  close: PropTypes.bool,
  onClose: PropTypes.func,
};

// The generic component which accepts a dataUrl and a configuration for rendering
export default function GenericOrderInfo({
  orderId,
  orderData,
  config,
  invoices = [],
  approvals = [],
  cargoXs = [],
  documents = [],
  transportationDocuments = [],
}) {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);

  const notesForReleaseOrder = {
    bolDate: orderData?.bolDate,
    bolNumber: orderData?.bolNumber,
    expectedArrivalDate: orderData?.expectedArrivalDate,
    invoiceNumber: orderData?.invoiceNumber,
    invoiceDate: orderData?.invoiceDate,
    customsReleaseDate: orderData?.customsReleaseDate,
    actualArrivalDate: orderData?.actualArrivalDate,
    releaseDate: orderData?.releaseDate,
    CustomsCertificateNumber: orderData?.customCertificate,
  };

  const [sectionIndex, setSectionIndex] = useState(
    sessionStorage.getItem("openSectionIndex")
      ? Number(sessionStorage.getItem("openSectionIndex"))
      : 0
  );
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [selectedObject, setSelectedObject] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const { loading: getAllLoading, getAll } = useGetManyRequests();
  const [isPageLoading, setIsPageLoading] = useState(false);
  let initialConfig = { formFields: [] };
  const [editConfig, setEditConfig] = useState(initialConfig);
  const [updateUrl, setUpdateUrl] = useState(ORDER_PRIMITIVE_DATA_URL);
  const [sectionData, setSectionData] = useState({});
  const [sectionName, setSectionName] = useState("Primitive Info Section");
  const [showInsertDocumentPopup, setShowInsertDocumentPopup] = useState(false);
  const [materialNameOptions, setMaterialNameOptions] = useState([]);
  const [iDCLinkageOptions, setIDCLinkageOptions] = useState([]);
  const [otherPrimitiveDataOptions, setOtherPrimitiveDataOptions] = useState(
    {}
  );
  const [otherForm4InfoOptions, setOtherForm4InfoOptions] = useState({});

  const [orderMaterials, setOrderMaterials] = useState(
    orderData?.materials?.map((item) => ({
      id: item.materialId,
      name: item.materialName,
    }))
  );

  const [orderIDCLinkages, setOrderIDCLinkages] = useState(
    orderData?.linkageInvoiceNumbers?.map((item) => ({
      id: item.orderId,
      name: item.invoiceNumber,
    }))
  );

  const [showStarPopup, setShowStarPopup] = useState(false);
  const [isOrderAlreadyStarred, setIsOrderAlreadyStarred] = useState(null);
  const [UnreadChat, setUnreadChat] = useState(null);
  const [isForm4PdfDownloading, setIsForm4PdfDownloading] = useState(false);
  const closeStarPopup = () => setShowStarPopup(false);
  const { loading: starOrderLoading, postItem: starOrder } = usePostItem();
  const { loading: unstarOrderLoading, postItem: unstarOrder } = usePostItem();

  const [vibrateReleaseButton, setVibrateReleaseButton] = useState(false);
  const [msdsNotes, setMSDSNotes] = useState([]);
  const [hasMissingMsds, setHasMissingMsds] = useState(false);

  const [openNotePoints, setOpenNotePoints] = useState(false);

  const handleClickOpenNotePoints = () => {
    setOpenNotePoints(true);
  };

  const handleCloseNotePoints = () => {
    setOpenNotePoints(false);
  };
  const openStarPopup = () => {
    setShowStarPopup(true);
  };
  const confirmStarCallBack = () => {
    setIsOrderAlreadyStarred(true);
    closeStarPopup();
  };

  const confirmUnstarCallBack = () => {
    setIsOrderAlreadyStarred(false);
    closeStarPopup();
  };

  const ConfirmStarPopUp = (OrderId) => {
    if (isOrderAlreadyStarred) {
      unstarOrder(
        UNSTAR_ORDERS_URL,
        { orderId: OrderId },
        confirmUnstarCallBack,
        `Order Unstarred Successfully!`,
        `Failed to Unstar Order!`
      );
    } else {
      starOrder(
        STAR_ORDERS_URL,
        { orderId: OrderId },
        confirmStarCallBack,
        `Order Starred Successfully!`,
        `Failed to Star Order!`
      );
    }
  };

  const printForm4Pdf = async () => {
    setIsForm4PdfDownloading(true);

    const printFrame = document.createElement("iframe");
    let pdfUrl;
    let isCleanedUp = false;

    const cleanup = () => {
      if (isCleanedUp) return;
      isCleanedUp = true;
      window.removeEventListener("afterprint", cleanup);
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
      printFrame.remove();
    };

    try {
      const response = await axiosInstance.get(`${FORM4_PDF_URL}/${orderId}`, {
        responseType: "blob",
      });
      pdfUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      printFrame.onload = () => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      };
      // Keep the PDF out of the visible page; the iframe exists only as the
      // print source while the browser's native print preview overlays this page.
      printFrame.style.position = "fixed";
      printFrame.style.left = "-10000px";
      printFrame.style.top = "0";
      printFrame.style.width = "100px";
      printFrame.style.height = "100px";
      printFrame.style.border = "0";
      printFrame.style.opacity = "0";
      printFrame.src = pdfUrl;
      window.addEventListener("afterprint", cleanup, { once: true });
      document.body.appendChild(printFrame);
    } catch (error) {
      cleanup();
      console.error("Form 4 PDF print failed:", error);
      dispatch(
        setSnackbar({
          open: true,
          message: "Form 4 PDF print failed",
          error: true,
        })
      );
    } finally {
      setIsForm4PdfDownloading(false);
    }
  };

  const handleClickOnReleaseButton = () => {
    setVibrateReleaseButton(true);
    setTimeout(() => setVibrateReleaseButton(false), 500);
  };

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showReleasePopup, setShowReleasePopup] = useState(false);
  const closeReleasePopup = () => setShowReleasePopup(false);

  const {
    requestMethod: updateData,
    isLoading: releaseOrderLoading,
    isError,
  } = useSendPutRequest(true);

  const openReleasePopup = () => {
    setShowReleasePopup(true);
  };
  const toggleChatPopup = () => {
    setIsChatOpen((prev) => !prev);
  };

  const { postItem: insertDocument } = usePostItem();
  const [insertDocumentLoading, setInsertDocumentLoading] = useState(false);
  const closeInsertDocumentPopup = () => setShowInsertDocumentPopup(false);
  const Currency = [
    { id: "AUD", name: "AUD" },
    { id: "AED", name: "AED" },
    { id: "CHF", name: "CHF" },
    { id: "CNY", name: "CNY" },
    { id: "EGP", name: "EGP" },
    { id: "EUR", name: "EUR" },
    { id: "GBP", name: "GBP" },
    { id: "INR", name: "INR" },
    { id: "QAR", name: "QAR" },
    { id: "SAR", name: "SAR" },
    { id: "USD", name: "USD" },
    { id: "NOK", name: "NOK" },
    { id: "ZAR", name: "ZAR" },
  ];
  const openInsertDocumentPopup = () => {
    setShowInsertDocumentPopup(true);
  };
  const ConfirmInsertDocument = async (document, file) => {
    setInsertDocumentLoading(true);
    const documentData = {
      ...document,
      orderId: orderId,
      fileAttached: file ? true : false,
    };
    const response = await insertDocument(
      SAVE_DOCUMENT,
      documentData,
      () => {},
      `Document Inserted Successfully!`,
      `Failed to insert Document!`
    );
    if (!response) return;
    if (file) {
      if (!response.preSignedUrl) {
        throw new Error("Presigned URL not received");
      }

      const { preSignedUrl, objectKey } = response;

      // Upload file to Minio
      await axios.put(preSignedUrl, file, {
        headers: {
          "Content-Type": "application/pdf",
        },
      });
    }
    window.location.reload();
    closeInsertDocumentPopup();
  };

  const ConfirmRelease = async (release) => {
    const releaseData = {
      ...release,
      id: orderId,
    };
    const response = await updateData(
      RELEASE_ORDER,
      releaseData,
      "Order Released Successfully!"
    );

    if (response) {
      //refresh the page to show the new document
      window.location.href = window.location.href;
    }
  };

  const getPrimitiveDataConfig = () => {
    return new Promise((resolve) => {
      const urls = [
        CATEGORIES_URL,
        CLEARANCE_AGENTS_URL,
        PLANNERS_URL,
        SUPPLIERS_URL,
        MATERIALS_URL,
      ];

      const optionsMapping = [
        {
          index: 0,
          key: "categoryName",
          id: "categoryId",
        },
        {
          index: 1,
          key: "agentName",
          id: "agentId",
        },
        {
          index: 2,
          key: "plannerName",
          id: "plannerId",
        },
        {
          index: 3,
          key: "supplierName",
          id: "supplierId",
        },
        {
          index: 4,
          key: "materialName",
          id: "materialId",
        },
      ];

      getAll(urls).then((res) => {
        let newOptions = {};

        optionsMapping.forEach(({ index, key, id }) => {
          const options =
            res[index]?.map((item) => ({
              name: item[key],
              id: String(item[id]),
            })) || [];
          newOptions[key] = options;
        });

        const selectedMaterialIds = orderMaterials.map((m) => String(m.id));

        const filteredMaterialNameOptions = [
          ...(newOptions["materialName"] ?? []),
        ].filter((option) => !selectedMaterialIds.includes(String(option.id)));

        setMaterialNameOptions(filteredMaterialNameOptions);

        setOtherPrimitiveDataOptions(newOptions);
        setUpdateUrl(ORDER_PRIMITIVE_DATA_URL);
        resolve();
      });
    });
  };

  useEffect(() => {
    if (!otherPrimitiveDataOptions["categoryName"]) return;

    const FormFields = [
      {
        size: 4,
        type: inputTypes.TEXT,
        key: "poNumber",
        label: "PO Number",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "category",
        label: "Category",
        options: otherPrimitiveDataOptions["categoryName"],
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "agent",
        label: "Agent",
        options: otherPrimitiveDataOptions["agentName"],
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "planner",
        label: "Planner",
        options: otherPrimitiveDataOptions["plannerName"],
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "supplier",
        label: "Supplier",
        options: otherPrimitiveDataOptions["supplierName"],
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "wayOfShipping",
        label: "Way Of Shipping",
        options: wayOfShippingOptions,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "state",
        label: "Order State",
        options: State,
        editable: false,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "origin",
        label: "Origin",
        options: origins,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "relativeCompany",
        label: "Relative Company",
        options: Company,
        editable: true,
      },
      {
        size: 6,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "materials",
        label: "Material Name",
        options: materialNameOptions,
        editable: true,
        isList: true,
        selectedObjects: orderMaterials,
        onselect: (selectedId, selectedItem) => {
          setOrderMaterials((prev) => [...prev, selectedItem]);
          setMaterialNameOptions((prev) =>
            prev.filter((item) => item.id !== selectedId)
          );
        },
        onDelete: (idToRemove) => {
          const removed = orderMaterials.find((item) => item.id === idToRemove);
          if (removed) {
            setOrderMaterials((prev) =>
              prev.filter((item) => item.id !== idToRemove)
            );
            setMaterialNameOptions((prev) => [...prev, removed]);
          }
        },
      },
      {
        size: 6,
        type: inputTypes.TEXT,
        key: "hsCode",
        label: "HsCode",
        editable: true,
      },
      {
        size: 12,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "isSample",
        label: "Is Sample",
        options: yesOrNoChoice,
        editable: true,
      },
      {
        size: 12,
        type: inputTypes.DATE,
        key: "pfiReceivalDate",
        label: "PFI Receival Date",
        editable: true,
      },
    ];

    setEditConfig((prev) => ({
      ...prev,
      formFields: FormFields,
    }));
  }, [materialNameOptions, otherPrimitiveDataOptions, orderMaterials]);

  const getOrderInformationConfig = () => {
    return new Promise((resolve) => {
      const urls = [SHIPPING_LINES_URL];

      const optionsMapping = [
        { index: 0, key: "shippingLineName", id: "shippingLineId" },
      ];

      getAll(urls).then((res) => {
        let newOptions = {};

        optionsMapping.forEach(({ index, key, id }) => {
          const options =
            res[index]?.map((item) => ({
              name: item[key],
              id: String(item[id]),
            })) || [];
          newOptions[key] = options; // Save for immediate use
        });
        const FormFields = [
          {
            size: 4,
            type: inputTypes.INTEGER_NUMBER,
            key: "acidNumber",
            label: "Acid Number",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.FLOAT_NUMBER,
            key: "grossWeight",
            label: "Gross Weight",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.FLOAT_NUMBER,
            key: "netWeight",
            label: "Net Weight",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "shippingCountry",
            label: "Shipping Country",
            options: origins,
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "portOfArrival",
            label: "Port Of Arrival",
            options: Ports,
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.TEXT,
            key: "shippingPort",
            label: "Shipping Port",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "loadability",
            label: "Loadability",
            options: loadability,
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "shippingLine",
            label: "Shipping Line Name",
            options: newOptions["shippingLineName"],
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.INTEGER_NUMBER,
            key: "container20",
            label: "Container 20",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.INTEGER_NUMBER,
            key: "container40",
            label: "Container 40",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.INTEGER_NUMBER,
            key: "pallets",
            label: "Pallets",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.INTEGER_NUMBER,
            key: "boxes",
            label: "boxes",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.INTEGER_NUMBER,
            key: "drums",
            label: "Drums",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.TEXT,
            key: "bookingNumber",
            label: "booking Number",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.TEXT,
            key: "generalComment",
            label: "General Comment",
            editable: true,
          },
          {
            size: 6,
            type: inputTypes.DATE,
            key: "acidCreationDate",
            label: "Acid Creation Date",
            editable: true,
          },
          {
            size: 6,
            type: inputTypes.DATE,
            key: "shippedOnboardDate",
            label: "Shipped Onboard Date",
            editable: true,
          },
        ];
        setUpdateUrl(ORDER_INFORMATION_URL);
        setEditConfig((prev) => ({
          ...prev,
          formFields: FormFields,
        }));

        resolve();
      });
    });
  };

  useEffect(() => {
    if (!otherForm4InfoOptions["invoiceNumber"]) return;

    const FormFields = [
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "bank",
        label: "Bank",
        options: otherForm4InfoOptions["bankName"],
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.FLOAT_NUMBER,
        key: "poAmount",
        label: "PO Amount",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "poCurrency",
        label: "PO Currency",
        options: Currency,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.TEXT,
        key: "invoiceNumber",
        label: "Invoice Number",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.DATE,
        key: "invoiceDate",
        label: "Invoice Date",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.FLOAT_NUMBER,
        key: "invoiceAmount",
        label: "Invoice Amount",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "invoiceCurrency",
        label: "Invoice Currency",
        options: Currency,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.TEXT,
        key: "bolNumber",
        label: "Bol Number",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.DATE,
        key: "bolDate",
        label: "Bol Date",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "maturityType",
        label: "Maturity Type",
        options: MaturityType,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.INTEGER_NUMBER,
        key: "maturityPeriod",
        label: "Maturity Period",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.DATE,
        key: "maturityDate",
        label: "Maturity Date",
        editable: false,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "transactionType",
        label: "Transaction Type",
        options: transactionType,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "incoTerm",
        label: "Inco Term",
        options: IncoTerm,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.TEXT,
        key: "idcNumber",
        label: "IDC Number",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.DATE,
        key: "form4SendingDate",
        label: "Form 4 Sending Date",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.DATE,
        key: "form4ReceivalDate",
        label: "Form 4 Receival Date",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.DATE,
        key: "uploadOnPortalDate",
        label: "Upload On Portal Date",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "proceededManually",
        label: "Proceeded Manually ?",
        options: yesOrNoChoice,
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.TEXT,
        key: "coraId",
        label: "CORA ID",
        editable: true,
      },
      {
        size: 4,
        type: inputTypes.DATE,
        key: "authorizationDate",
        label: "Authorization Date",
        editable: true,
      },
      {
        size: 12,
        type: inputTypes.SEARCHABLE_DROPDOWN,
        key: "linkageInvoiceNumbers",
        label: "IDC linkage",
        options: iDCLinkageOptions,
        editable:
          data?.invoiceNumber && data?.invoiceNumber != "" ? true : false,
        isList: true,
        note: "Please fill and save the invoice number field before adding IDC linkage",
        activateNote:
          data?.invoiceNumber && data?.invoiceNumber != "" ? false : true,
        selectedObjects: orderIDCLinkages,
        onselect: (selectedId, selectedItem) => {
          setOrderIDCLinkages((prev) => [...prev, selectedItem]);
          setIDCLinkageOptions((prev) =>
            prev.filter((item) => item.id !== selectedId)
          );
        },
        onDelete: (idToRemove) => {
          const removed = orderIDCLinkages.find(
            (item) => item.id === idToRemove
          );
          if (removed) {
            setOrderIDCLinkages((prev) =>
              prev.filter((item) => item.id !== idToRemove)
            );
            setIDCLinkageOptions((prev) => [...prev, removed]);
          }
        },
      },
    ];

    setEditConfig((prev) => ({
      ...prev,
      formFields: FormFields,
    }));
  }, [iDCLinkageOptions, otherForm4InfoOptions, orderIDCLinkages]);

  const getForm4InformationConfig = () => {
    return new Promise((resolve) => {
      const urls = [BANKS_URL, `${GET_LINKAGE_INVOICE_NUMBERS}/${orderId}`];

      const optionsMapping = [
        { index: 0, key: "bankName", id: "bankId" },
        { index: 1, key: "invoiceNumber", id: "orderId" },
      ];

      getAll(urls).then((res) => {
        let newOptions = {};

        optionsMapping.forEach(({ index, key, id }) => {
          const options =
            res[index]?.map((item) => ({
              name: item[key],
              id: String(item[id]),
            })) || [];
          newOptions[key] = options;
        });

        const selectedIDCLinkage = orderIDCLinkages.map((m) => String(m.id));

        const filteredIDCLinkageOptions = [
          ...(newOptions["invoiceNumber"] ?? []),
        ].filter((option) => !selectedIDCLinkage.includes(String(option.id)));

        setIDCLinkageOptions(filteredIDCLinkageOptions);
        setOtherForm4InfoOptions(newOptions);
        setUpdateUrl(ORDER_FORM4_INFORMATION_URL);
        resolve();
      });
    });
  };

  const getOrderSpecificInformationConfig = () => {
    return new Promise((resolve) => {
      const urls = [WAREHOUSES_URL, STORAGE_LOCATIONS_URL];

      const optionsMapping = [
        { index: 0, key: "warehouseName", id: "warehouseId" },
        { index: 1, key: "locationName", id: "locationId" },
      ];

      getAll(urls).then((res) => {
        let newOptions = {};
        optionsMapping.forEach(({ index, key, id }) => {
          const options =
            res[index]?.map((item) => ({
              name: item[key],
              id: String(item[id]),
            })) || [];
          newOptions[key] = options;
        });

        const FormFields = [
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "warehouse",
            label: "Warehouse",
            options: newOptions["warehouseName"],
            editable: orderData.state == "RELEASED",
          },
          {
            size: 4,
            type: inputTypes.TEXT,
            key: "pendingOn",
            label: "Pending On",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.TEXT,
            key: "orderNumber",
            label: "Order Number",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "storageLocation",
            label: "Storage Location",
            options: newOptions["locationName"],
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.INTEGER_NUMBER,
            key: "customCertificate",
            label: "Custom Certificate",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.TEXT,
            key: "ibdNumber",
            label: "IBD Number",
            editable: orderData.state == "RELEASED",
          },
          {
            size: 4,
            type: inputTypes.FLOAT_NUMBER,
            key: "fod",
            label: "FOD",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "bankLinkage",
            label: "Bank Linkage",
            options: yesOrNoChoice,
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.SEARCHABLE_DROPDOWN,
            key: "nonRelevant",
            label: "Non Relevant",
            options: yesOrNoChoice,
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "ibdDate",
            label: "IBD Date",
            editable: orderData.state == "RELEASED",
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "customsReleaseDate",
            label: "Customs Release Date",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "warehouseArrivalDate",
            label: "Warehouse Arrival Date",
            editable: orderData.state == "RELEASED",
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "releaseDate",
            label: "Release Date",
            editable: orderData.state == "RELEASED",
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "expectedArrivalDate",
            label: "Expected Arrival Date",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "actualArrivalDate",
            label: "Actual Arrival Date",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "customsApprovalDate",
            label: "Customs Approval Date",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "expectedReleaseDate",
            label: "Expected Release Date",
            editable: true,
          },
          {
            size: 4,
            type: inputTypes.DATE,
            key: "storageDate",
            label: "Storage Date",
            editable: true,
          },
        ];
        setUpdateUrl(ORDER_SPECIFIC_INFORMATION_URL);
        setEditConfig((prev) => ({
          ...prev,
          formFields: FormFields,
        }));
        resolve();
      });
    });
  };

  const getTransportationDetailsConfig = () => {
    return new Promise((resolve) => {
      const FormFields = [
        {
          size: 6,
          type: inputTypes.TEXT,
          key: "trucksNumbers",
          label: "Trucks Numbers",
          editable: true,
        },
        {
          size: 6,
          type: inputTypes.TEXT,
          key: "transportationRemarks",
          label: "Transportation Remarks",
          editable: true,
        },
        {
          size: 4,
          type: inputTypes.DATE,
          key: "timeOfLoading",
          label: "Time Of Loading",
          editable: true,
          views: ["year", "month", "day", "hours", "minutes"],
          format: "DD/MM/YYYY HH:mm",
        },
        {
          size: 4,
          type: inputTypes.DATE,
          key: "warehouseEntry",
          label: "Warehouse Entry",
          editable: true,
          views: ["year", "month", "day", "hours", "minutes"],
          format: "DD/MM/YYYY HH:mm",
        },
        {
          size: 4,
          type: inputTypes.DATE,
          key: "warehouseExit",
          label: "Warehouse Exit",
          editable: true,
          views: ["year", "month", "day", "hours", "minutes"],
          format: "DD/MM/YYYY HH:mm",
        },
      ];

      setUpdateUrl(ORDER_TRANSPORTATION_DETAILS_URL);
      setEditConfig((prev) => ({
        ...prev,
        formFields: FormFields,
      }));

      setSectionData({
        trucksNumbers: data?.trucksNumbers ?? "",
        transportationRemarks: data?.transportationRemarks ?? "",
        timeOfLoading: normalizeDateTimeValue(data?.timeOfLoading),
        warehouseEntry: normalizeDateTimeValue(data?.warehouseEntry),
        warehouseExit: normalizeDateTimeValue(data?.warehouseExit),
      });

      resolve();
    });
  };

  const onFullInfoEdit = async (pageName) => {
    setIsPageLoading(true);
    setShowEditPopup(true);
    if (pageName === "Primitive Info") {
      setSectionName("Primitive Info");
      await getPrimitiveDataConfig();
    } else if (pageName === "Order Info") {
      setSectionName("Order Info");
      await getOrderInformationConfig();
    } else if (pageName === "Form 4 Info") {
      setSectionName("Form 4 Info");
      await getForm4InformationConfig(); // Wait for data to load
    } else if (pageName === "Order Specific Info") {
      setSectionName("Order Specific Info");
      await getOrderSpecificInformationConfig(); // Wait for data to load
    } else if (pageName === "Transportation") {
      setSectionName("Transportation");
      await getTransportationDetailsConfig();
    }
    setIsPageLoading(false);
  };

  const getItem = useGetItem();

  useEffect(() => {
    const fetchStarState = async () => {
      const starState = await getItem(IS_ORDER_STARRED, orderId);
      if (starState !== undefined && starState !== null) {
        setIsOrderAlreadyStarred( Boolean(starState));
      }
    };

    if (orderId) {
      fetchStarState();
    }
  }, [getItem, orderId]);

  useEffect(() => {
    setData({
      ...orderData,
      isSample: orderData.isSample === true ? "YES" : "NO",
      proceededManually: orderData.proceededManually === true ? "YES" : "NO",
    });
    const fetchComments = async () => {
      const commentData = await getItem(PENDING_ORDER_COMMENTS, `${orderId}`);
      if (commentData) {
        setUnreadChat(commentData);
      }
    };

    fetchComments();
  }, []);

  useEffect(() => {
    setIsLoading(true);

    const promises = [getItem(GET_MSDS_FILE_FOR_ORDER, orderId)];

    Promise.all(promises)
      .then(([MSDSdata]) => {
        setHasMissingMsds(MSDSdata.some((item) => !item.exists));
        const transformed = MSDSdata.map((item) => ({
          label: `${item.supplierName} - ${item.materialName}`,
          value: item.exists ? "Exist" : "Not exist",
        }));
        setMSDSNotes(transformed);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, [orderId]);

  const handleChange = (event, newValue) => {
    setSelectedObject(null);
    setSectionData([]);
    setEditConfig(initialConfig);
    sessionStorage.setItem("openSectionIndex", newValue);
    setSectionIndex(newValue);
  };
  const confirmEdit = async (newData) => {
    let updatedOrderData = {
      ...newData,
      orderId: Number(orderId),
    };

    if (sectionName === "Primitive Info") {
      const materialIds = orderMaterials.map((m) => Number(m.id));

      updatedOrderData = {
        ...updatedOrderData,
        materials: materialIds,
        isSample: newData.isSample === "YES", // ✅ converts "YES"/"NO" -> true/false
      };
    } else if (sectionName === "Form 4 Info") {
      const iDCLinkages = orderIDCLinkages.map((l) => Number(l.id));

      updatedOrderData = {
        ...updatedOrderData,
        linkageInvoiceNumbers: iDCLinkages,
        proceededManually: newData.proceededManually === "YES", // ✅ converts "YES"/"NO" -> true/false
      };
    }

    Object.keys(updatedOrderData).forEach((key) => {
      if (updatedOrderData[key] === "") {
        updatedOrderData[key] = null;
      }
    });

    ["timeOfLoading", "warehouseEntry", "warehouseExit"].forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(updatedOrderData, field)) {
        updatedOrderData[field] = normalizeDateTimeValue(
          updatedOrderData[field]
        );
      }
    });

    const response = await putItem(
      updateUrl,
      updatedOrderData,
      `Order Edited Successfully!`
    );

    if (response) {
      window.location.reload();
      closeEditPopup();
    }
  };

  const closeEditPopup = () => setShowEditPopup(false);

  const { isLoading: PutItemLoading, requestMethod: putItem } =
    useSendPutRequest(true);

  const formatDate = (date) => {
    if (!date) return "N/A";

    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString("en-GB");
  };
  const getFieldValue = (fieldItem, data) => {
    const fieldParts = fieldItem.field.split(".");

    if (!data[fieldParts[0]]) {
      console.warn(`Field ${fieldParts[0]} does not exist in data`, data);
      return "";
    }

    if (fieldParts.length === 1) {
      return data[fieldParts[0]] || "";
    }
    return data[fieldParts[0]][fieldItem.fieldId] || "";
  };

  const onEditSection = (tab) => {
    tab.subsections.map((section) => {
      section?.fields?.map((fieldItem) => {
        setSectionData((prev) => ({
          ...prev,
          [fieldItem.field.split(".")[0]]: getFieldValue(fieldItem, data),
        }));
      });
    });
    onFullInfoEdit(tab.label);
  };
  //*********************** Primitive Edit Section ***********************//
  return (
    <>
      <GenericOrderInfoStyles>
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={sectionIndex}
          onChange={handleChange}
          sx={TabsStyles}
        >
          {config.tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} {...a11yProps(index)} />
          ))}
        </Tabs>

        {config.tabs.map((tab, tabIndex) => (
          <TabPanel
            key={tabIndex}
            value={sectionIndex}
            index={tabIndex}
            sx={{ flex: 1, overflowY: "auto" }}
            style={{ width: "80vw" }}
          >
            {/* Add the edit button here, inside each section */}

            {/* Position the edit button properly */}
            <Box sx={{ position: "relative", width: "100%" }}>
              {!(
                tab.label === "Payments" ||
                tab.label === "Approvals Section" ||
                tab.label === "CargoX Section" ||
                tab.label === "Documents & CargoXs" ||
                tab.label === "Conditions Section" ||
                tab.label === "Transportation"
              ) &&
                tab.editable && (
                  <>
                    {(user.roles?.includes("CLEARANCE_COORDINATOR")
                      ? orderData.createdBy === user.email
                      : true) && (
                      <Box
                        sx={{
                          position: "absolute",
                          right: 10,
                          top: 13,
                          zIndex: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {tab.label === "Form 4 Info" && (
                          <Tooltip title="Print Form 4 PDF">
                            <IconButton
                              size="small"
                              disabled={isForm4PdfDownloading}
                              onClick={(e) => {
                                e.stopPropagation();
                                printForm4Pdf();
                              }}
                            >
                              <PictureAsPdfIcon
                                fontSize="small"
                                sx={{ color: theme.palette.primary.main }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditSection(tab);
                          }}
                        >
                          <EditIcon
                            fontSize="small"
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </IconButton>
                      </Box>
                    )}
                  </>
                )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  position: "relative",
                }}
                style={{ padding: "0px" }}
              >
                {tab.subsections.map((section, secIndex) => {
                  // For a normal subsection, render the fields as InfoItems.
                  return (
                    <SubSection
                      key={secIndex}
                      title={section.title}
                      textAfterTitle={
                        !section.title.includes("Date")
                          ? `(PO Number : ${orderData.poNumber})`
                          : ""
                      }
                      width={section.width || "500px"}
                      mr={section.mr}
                      pr={section.pr}
                      action={
                        <>
                          {!isLoading && tab.label === "Approvals Section" ? (
                            <>
                              <Tooltip title="Notes For Approval">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickOpenNotePoints();
                                  }}
                                  sx={{
                                    color: hasMissingMsds
                                      ? theme.palette.error.main
                                      : "#4CAF50",
                                  }}
                                >
                                  <TipsAndUpdatesIcon fontSize="medium" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip
                                title={
                                  orderData?.acidNumber
                                    ? `ACID Number: ${orderData.acidNumber}`
                                    : "No ACID Number"
                                }
                              >
                                <IconButton
                                  onClick={(e) => e.stopPropagation()}
                                  size="small"
                                  sx={{
                                    color: orderData?.acidNumber
                                      ? "success.main"
                                      : theme.palette.error.main,
                                  }}
                                >
                                  {orderData?.acidNumber ? (
                                    <VerifiedIcon fontSize="small" />
                                  ) : (
                                    <ReportProblemIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : null}

                          {(tab.label === "Payments" ||
                            tab.label === "Approvals Section") &&
                          tab.editable ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (tab.label === "Approvals Section") {
                                    navigate(
                                      DefaultRoutes.approval.replace(
                                        ":orderId",
                                        encryptId(Number(orderId))
                                      )
                                    );
                                  } else if (tab.label === "Payments") {
                                    navigate(
                                      `${
                                        DefaultRoutes.invoice
                                      }?orderId=${encryptId(
                                        orderId
                                      )}&poNumber=${encryptId(
                                        orderData.poNumber
                                      )}`
                                    );
                                  }
                                }}
                              >
                                <Tooltip title="Add New Item">
                                  <AddIcon
                                    sx={{
                                      fontSize: "1.5rem",
                                      color: theme.palette.primary.main,
                                    }}
                                  />
                                </Tooltip>
                              </IconButton>
                            </>
                          ) : tab.label === "Documents & CargoXs" &&
                            tab.editable ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    DefaultRoutes.cargox.replace(
                                      ":orderId",
                                      encryptId(orderId)
                                    )
                                  );
                                }}
                              >
                                <Tooltip title="Add CargoX">
                                  <LocalShippingIcon
                                    sx={{
                                      fontSize: "1.5rem",
                                      color: theme.palette.primary.main,
                                      mr: -1.2,
                                    }}
                                  />
                                </Tooltip>
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openInsertDocumentPopup();
                                }}
                              >
                                <Tooltip title="Add Document">
                                  <NoteAddRoundedIcon
                                    sx={{
                                      fontSize: "1.5rem",
                                      color: theme.palette.primary.main,
                                    }}
                                  />
                                </Tooltip>
                              </IconButton>
                            </>
                          ) : null}
                          {!section.title.includes("Date") &&
                            tab.label === "Transportation" &&
                            tab.editable &&
                            (user.roles?.includes("CLEARANCE_COORDINATOR")
                              ? orderData.createdBy === user.email
                              : true) && (
                              <Tooltip title="Edit Transportation">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditSection(tab);
                                  }}
                                >
                                  <EditIcon
                                    fontSize="small"
                                    sx={{ color: theme.palette.primary.main }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          {!section.title.includes("Date") &&
                            orderData.state === "READY_TO_RELEASE" &&
                            orderData.state !== "RELEASED" &&
                            (user.roles?.includes("CLEARANCE_COORDINATOR")
                              ? orderData.createdBy === user.email
                              : false) && (
                              <Tooltip title="Release Order">
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => {
                                    openReleasePopup(orderId);
                                  }}
                                  sx={{ position: "relative" }}
                                >
                                  <LockOpenRoundedIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          {!section.title.includes("Date") &&
                            orderData.state !== "READY_TO_RELEASE" &&
                            orderData.state !== "RELEASED" &&
                            (user.roles?.includes("CLEARANCE_COORDINATOR")
                              ? orderData.createdBy === user.email
                              : false) && (
                              <Tooltip title="Order Cannot Be Released Waiting For Complete Info">
                                <IconButton
                                  color="disabled"
                                  size="small"
                                  onClick={handleClickOnReleaseButton}
                                  sx={{ position: "relative" }}
                                >
                                  <LockClockRoundedIcon
                                    sx={{
                                      ...(vibrateReleaseButton && {
                                        animation:
                                          "vibrate 0.3s linear infinite",
                                      }),
                                      "@keyframes vibrate": {
                                        "0%": { transform: "translate(0)" },
                                        "20%": {
                                          transform: "translate(-2px, 2px)",
                                        },
                                        "40%": {
                                          transform: "translate(-2px, -2px)",
                                        },
                                        "60%": {
                                          transform: "translate(2px, 2px)",
                                        },
                                        "80%": {
                                          transform: "translate(2px, -2px)",
                                        },
                                        "100%": { transform: "translate(0)" },
                                      },
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          {!section.title.includes("Date") && (
                            <Tooltip title="Add Comment">
                              <IconButton
                                color="info"
                                size="small"
                                onClick={() => {
                                  setUnreadChat(null);
                                  toggleChatPopup();
                                }}
                                sx={{ position: "relative" }}
                              >
                                <CommentRoundedIcon />
                                {(() => {
                                  // If we have pending chats, show dots
                                  if (UnreadChat) {
                                    return (
                                      <>
                                        {/* First dot - show if first boolean is true */}
                                        {UnreadChat?.groupChatPending && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              top: 2,
                                              right: 2,
                                              width: 10,
                                              height: 10,
                                              borderRadius: "50%",
                                              backgroundColor: "error.main",
                                              border: "1px solid white",
                                            }}
                                          />
                                        )}

                                        {/* Second dot - show if second boolean is true */}
                                        {UnreadChat?.plannerChatPending && (
                                          <Box
                                            sx={{
                                              position: "absolute",
                                              bottom: 2,
                                              right: 2,
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "error.main",
                                              border: "1px solid white",
                                            }}
                                          />
                                        )}
                                      </>
                                    );
                                  }
                                  return null;
                                })()}
                              </IconButton>
                            </Tooltip>
                          )}
                          {!section.title.includes("Date") &&
                            (user.roles?.includes("CLEARANCE_COORDINATOR")
                              ? orderData.createdBy === user.email
                              : true) &&
                            !user.roles?.includes("QUALITY_MEMBER") &&
                            !user.roles?.includes("WAREHOUSE_MEMBER") &&
                            isOrderAlreadyStarred !=null && (
                              <Tooltip title="Star Order">
                                <IconButton
                                  color="info"
                                  onClick={() => openStarPopup(orderId)}
                                  size="small"
                                >
                                  {isOrderAlreadyStarred ? (
                                    <StarIcon sx={{ color: "red !important" }} />
                                  ) : (
                                    <StarIcon sx={{ color: "yellow !important" }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                        </>
                      }
                    >
                      {section.type === "Invoices" ? (
                        <InvoiceList
                          invoices={invoices}
                          selectedObject={selectedObject}
                          setSelectedObject={setSelectedObject}
                          editable={tab.editable}
                        />
                      ) : section.type === "CargoX" ? (
                        <CargoXList
                          orderId={orderId}
                          cargoXs={cargoXs}
                          selectedObject={selectedObject}
                          setSelectedObject={setSelectedObject}
                          editable={tab.editable}
                        />
                      ) : section.type === "Approvals" ? (
                        <ApprovalList
                          orderId={orderId}
                          approvals={approvals}
                          selectedObject={selectedObject}
                          setSelectedObject={setSelectedObject}
                          editable={tab.editable}
                        />
                      ) : section.type === "Documents" ? (
                        <DocumentList
                          orderId={orderId}
                          documents={documents}
                          selectedObject={selectedObject}
                          setSelectedObject={setSelectedObject}
                          cargoXs={cargoXs}
                          editable={tab.editable}
                        />
                      ) : section.type === "Conditions" ? (
                        <ConditionTable
                          orderId={orderId}
                          selectedObject={selectedObject}
                          setSelectedObject={setSelectedObject}
                          editable={tab.editable}
                        />
                      ) : section.type === "Transportation" ? (
                        <TransportationSection
                          orderId={orderId}
                          transportationDocuments={transportationDocuments}
                          trucksNumbers={data?.trucksNumbers}
                          transportationRemarks={data?.transportationRemarks}
                          timeOfLoading={data?.timeOfLoading}
                          warehouseEntry={data?.warehouseEntry}
                          warehouseExit={data?.warehouseExit}
                        />
                      ) : (
                        section?.fields?.map((fieldItem, fieldIndex) => (
                          <InfoItem
                            key={fieldIndex}
                            label={fieldItem.label}
                            field={fieldItem.field}
                            data={data}
                            formatter={
                              fieldItem.formatter === "date"
                                ? formatDate
                                : (textValue) =>
                                    formatText(
                                      textValue,
                                      fieldItem.capitalText || false
                                    )
                            }
                          />
                        ))
                      )}
                    </SubSection>
                  );
                })}
                {config.tabs[tabIndex].preview &&
                  (() => {
                    const isClosed = !selectedObject;
                    const defaultWidth =
                      config.tabs[tabIndex].preview.width || "40vw";
                    const previewWidth = isClosed ? "50px" : defaultWidth;

                    return (
                      <SubSection
                        title={config.tabs[tabIndex].preview.title}
                        width={previewWidth}
                        last
                        close={!isClosed}
                        onClose={() => setSelectedObject(null)}
                      >
                        {isClosed ? (
                          <Box sx={PreviewDocumentBoxStyles}>
                            <Typography variant="h6" color="textSecondary">
                              {!selectedObject
                                ? config.tabs[tabIndex].preview.placeholder ||
                                  "Select a File to preview"
                                : "Original Documents Do Not Support Uploaded Files"}
                            </Typography>
                          </Box>
                        ) : selectedObject.presignedUrl ? (
                          <PDFViewer fileUrl={selectedObject.presignedUrl} />
                        ) : (
                          <Box sx={PreviewDocumentBoxStyles}>
                            <Typography variant="h6" color="textSecondary">
                              No PDF available for this document
                            </Typography>
                          </Box>
                        )}
                      </SubSection>
                    );
                  })()}
                {EditSectionPopup(
                  sectionName,
                  showEditPopup,
                  closeEditPopup,
                  confirmEdit,
                  PutItemLoading,
                  editConfig.formFields,
                  sectionData,
                  false,
                  isPageLoading
                )}
                <InsertDocumentPopup
                  showPopup={showInsertDocumentPopup}
                  closePopup={closeInsertDocumentPopup}
                  Confirm={ConfirmInsertDocument}
                  loading={insertDocumentLoading}
                  documentData={null}
                  isEditing={false}
                  selectedId={orderId}
                />
                {DeletePopup(
                  "Order",
                  showStarPopup,
                  closeStarPopup,
                  ConfirmStarPopUp,
                  orderId,
                  isOrderAlreadyStarred ? unstarOrderLoading : starOrderLoading,
                  isOrderAlreadyStarred ? "Unstar" : "Star"
                )}
                {ReleaseOrderPopup(
                  showReleasePopup,
                  closeReleasePopup,
                  ConfirmRelease,
                  releaseOrderLoading,
                  notesForReleaseOrder
                )}
                <ChatPopup
                  open={isChatOpen}
                  onClose={toggleChatPopup}
                  orderId={orderId}
                />
              </Box>
            </Box>
          </TabPanel>
        ))}
      </GenericOrderInfoStyles>
      <SimpleNotesPopup
        openNotePoints={openNotePoints}
        onClose={handleCloseNotePoints}
        noteData={msdsNotes}
      />
    </>
  );
}

GenericOrderInfo.propTypes = {
  orderData: PropTypes.object.isRequired,
  config: PropTypes.shape({
    tabs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        subsections: PropTypes.arrayOf(
          PropTypes.shape({
            title: PropTypes.string.isRequired, // For normal sections:
            fields: PropTypes.arrayOf(
              PropTypes.shape({
                label: PropTypes.string.isRequired,
                field: PropTypes.string.isRequired, // "formatter" can be a function or a string (e.g. "date")
                formatter: PropTypes.oneOfType([
                  PropTypes.func,
                  PropTypes.string,
                ]),
              })
            ), // For special sections like documents:
            type: PropTypes.string,
            width: PropTypes.string,
            mr: PropTypes.number,
            pr: PropTypes.number,
          })
        ), // Optionally, a preview section (e.g. for document preview)
        preview: PropTypes.shape({
          title: PropTypes.string.isRequired,
          width: PropTypes.string,
          placeholder: PropTypes.string,
        }),
      })
    ).isRequired,
  }).isRequired,
  transportationDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      documentName: PropTypes.string,
      presignedUrl: PropTypes.string,
      objectKey: PropTypes.string,
    })
  ),
};
